/**
 * OTP verification screen — 6-digit code sent via WhatsApp.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../../src/stores/authStore';
import { useToastStore } from '../../src/stores/toastStore';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../../src/utils/design-tokens';
import { needsInitialSetup } from '../../src/utils/onboarding';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;
const OTP_EXPIRY_SECS = 300; // 5 minutes

export default function OtpScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ phone?: string | string[]; isTestAccount?: string | string[]; inferredTz?: string | string[] }>();
  const phoneParam = readParam(params.phone);
  const isTestAccount = readParam(params.isTestAccount);
  const inferredTz = readParam(params.inferredTz);
  const { t } = useTranslation();
  const verifyOtp = useAuthStore((s) => s.verifyOtp);
  const requestOtp = useAuthStore((s) => s.requestOtp);
  const setPhone = useAuthStore((s) => s.setPhone);
  const storePhone = useAuthStore((s) => s.phone);
  const currentPhone = phoneParam || storePhone;
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);

  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [otpExpiry, setOtpExpiry] = useState(OTP_EXPIRY_SECS);
  const inputRefs = useRef<(TextInput | null)[]>([]);

  useEffect(() => {
    if (phoneParam && phoneParam !== storePhone) setPhone(phoneParam);
  }, [phoneParam, setPhone, storePhone]);

  useEffect(() => {
    if (!currentPhone) router.replace('/(auth)/phone');
  }, [currentPhone, router]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // OTP expiry timer
  useEffect(() => {
    if (otpExpiry <= 0) return;
    const timer = setInterval(() => setOtpExpiry((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [otpExpiry]);

  // Auto-verify for test accounts
  useEffect(() => {
    if (isTestAccount === '1' && currentPhone) {
      const testCode = '000000'.split('');
      setDigits(testCode);
      // Small delay to let UI render, then auto-submit
      const timer = setTimeout(() => handleVerify('000000'), 300);
      return () => clearTimeout(timer);
    }
  }, [currentPhone, isTestAccount]);

  const handleDigitChange = (index: number, value: string) => {
    if (otpError) setOtpError('');
    if (value.length > 1) {
      // Handle paste of full code
      const pasted = value.replace(/[^0-9]/g, '').slice(0, OTP_LENGTH).split('');
      const newDigits = [...digits];
      pasted.forEach((d, i) => {
        if (index + i < OTP_LENGTH) newDigits[index + i] = d;
      });
      setDigits(newDigits);

      const lastIdx = Math.min(index + pasted.length, OTP_LENGTH) - 1;
      inputRefs.current[lastIdx]?.focus();

      // Auto-submit if all filled
      if (newDigits.every((d) => d !== '')) {
        handleVerify(newDigits.join(''));
      }
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit
    if (value && newDigits.every((d) => d !== '')) {
      handleVerify(newDigits.join(''));
    }
  };

  const handleKeyPress = (index: number, key: string) => {
    if (key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newDigits = [...digits];
      newDigits[index - 1] = '';
      setDigits(newDigits);
    }
  };

  const handleVerify = async (otp?: string) => {
    if (!currentPhone) {
      router.replace('/(auth)/phone');
      return;
    }
    const code = otp || digits.join('');
    if (code.length !== OTP_LENGTH) {
      setOtpError(t('auth.enterFullCode'));
      return;
    }
    setOtpError('');
    setPhone(currentPhone);

    setIsLoading(true);
    try {
      const user = await verifyOtp(code);
      if (needsInitialSetup(user)) {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const tz = inferredTz ? `?inferredTz=${encodeURIComponent(inferredTz)}` : '';
          window.location.replace(`/setup${tz}`);
          return;
        }
        router.replace({ pathname: '/(auth)/setup', params: { inferredTz: inferredTz || '' } });
      } else {
        // On web, use a hard navigation so the SPA fully re-mounts with the
        // new authenticated session. Expo Router's client-side replace can
        // race with the auth-gate effect (same-URL dedupe + grouped route
        // segments) and leave the user stranded on /otp until they hit refresh.
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.location.replace('/chat');
          return;
        }
        router.replace('/(tabs)/chat');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('auth.invalidOtp');
      setOtpError(msg);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !currentPhone) return;
    try {
      await requestOtp(currentPhone);
      setCooldown(RESEND_COOLDOWN);
      setOtpExpiry(OTP_EXPIRY_SECS);
      useToastStore.getState().showToast(t('auth.otpResent'), t('auth.otpResentDesc', 'Check your WhatsApp for a new code.'));
    } catch {
      useToastStore.getState().showToast(t('auth.error'), t('auth.otpFailed'));
    }
  };

  const handleEditPhone = () => {
    setPhone(currentPhone || '');
    router.replace({ pathname: '/(auth)/phone' });
  };

  if (!currentPhone) return null;

  return (
    <KeyboardAvoidingView
      style={s.container}
      behavior="padding"
    >
      <View style={s.content}>
        <Pressable style={s.backRow} onPress={handleEditPhone} accessibilityRole="button" accessibilityLabel={t('auth.backToPhone')} hitSlop={8}>
          <Ionicons name="arrow-back" size={18} color={colors.primary} />
          <Text style={s.backText}>{t('auth.backToPhone')}</Text>
        </Pressable>

        <Text style={s.title}>{t('auth.verifyTitle')}</Text>
        <Text style={s.subtitle}>
          {t('auth.otpSentTo', { phone: currentPhone })}
        </Text>
        <TouchableOpacity onPress={handleEditPhone} accessibilityRole="button" accessibilityLabel={t('auth.editPhone')} hitSlop={8}>
          <Text style={s.editPhoneText}>{t('auth.editPhone')}</Text>
        </TouchableOpacity>

        {/* Auth progress stepper */}
        <View style={s.stepper}>
          <View style={[s.stepDot, s.stepDone]} /><View style={s.stepLine} /><View style={[s.stepDot, s.stepActive]} /><View style={s.stepLine} /><View style={s.stepDot} />
        </View>

        {/* OTP expiry indicator */}
        {otpExpiry > 0 && (
          <Text style={[s.expiryText, otpExpiry <= 60 && { color: colors.error }]}>
            {t('auth.codeExpires', { defaultValue: 'Code expires in {{time}}', time: `${Math.floor(otpExpiry / 60)}:${String(otpExpiry % 60).padStart(2, '0')}` })}
          </Text>
        )}
        {otpExpiry <= 0 && (
          <Text style={[s.expiryText, { color: colors.error }]}>
            {t('auth.codeExpired', { defaultValue: 'Code expired — tap resend below' })}
          </Text>
        )}

        {/* OTP digit inputs */}
        <View style={s.otpRow}>
          {digits.map((digit, index) => (
            <TextInput
              key={index}
              ref={(ref) => { inputRefs.current[index] = ref; }}
              style={[s.otpInput, digit && s.otpInputFilled, !!otpError && s.otpInputError]}
              value={digit}
              onChangeText={(v) => handleDigitChange(index, v)}
              onKeyPress={({ nativeEvent }) => handleKeyPress(index, nativeEvent.key)}
              keyboardType="number-pad"
              maxLength={index === 0 ? OTP_LENGTH : 1}
              autoFocus={index === 0}
              selectTextOnFocus
              accessibilityLabel={`${t('auth.verifyTitle')} ${index + 1}`}
            />
          ))}
        </View>
        {!!otpError && <Text style={s.errorText}>{otpError}</Text>}

        {/* Verify button */}
        <TouchableOpacity
  onPress={() => handleVerify()}
  disabled={isLoading}
  accessibilityRole="button"
  accessibilityLabel={t('auth.verify')}
>
  <LinearGradient
    colors={isLoading ? [colors.textSecondary, colors.textSecondary] : ['#64B5F6', '#1E88E5']}
    start={{ x: 0, y: 0 }}
    end={{ x: 1, y: 1 }}
    style={s.button}
  >
    {isLoading ? (
      <ActivityIndicator color="#fff" />
    ) : (
      <Text style={s.buttonText}>{t('auth.verify')}</Text>
    )}
  </LinearGradient>
</TouchableOpacity>

        {/* Resend */}
        <TouchableOpacity
          onPress={handleResend}
          disabled={cooldown > 0}
          style={s.resendButton}
          accessibilityRole="button"
          accessibilityLabel={cooldown > 0 ? t('auth.resendIn', { seconds: cooldown }) : t('auth.resendCode')}
        >
          <Text style={[s.resendText, cooldown > 0 && s.resendDisabled]}>
            {cooldown > 0
              ? t('auth.resendIn', { seconds: cooldown })
              : t('auth.resendCode')}
          </Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

function readParam(value?: string | string[]) {
  if (Array.isArray(value)) return value[0] || '';
  return value || '';
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  backRow: {
    position: 'absolute',
    top: 72,
    left: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backText: {
    color: colors.primary,
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
  },
  title: {
    fontSize: FONT_SIZE.xl,
    fontFamily: 'PlusJakartaSans_700Bold',
    color: colors.text,
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  subtitle: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_400Regular',
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  editPhoneText: {
    color: colors.primary,
    textAlign: 'center',
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_600SemiBold',
    marginBottom: SPACING.md,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  stepDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.border,
  },
  stepDone: {
    backgroundColor: colors.primary,
  },
  stepActive: {
    backgroundColor: colors.primary,
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  stepLine: {
    width: 28,
    height: 2,
    backgroundColor: colors.border,
    marginHorizontal: 4,
  },
  expiryText: {
    textAlign: 'center',
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_500Medium',
    color: colors.textSecondary,
    marginBottom: SPACING.base,
  },
  otpRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: SPACING.md,
    marginBottom: SPACING['2xl'],
  },
  otpInput: {
    width: 48,
    height: 56,
    borderRadius: RADIUS.md,
    backgroundColor: colors.card,
    borderWidth: 1.5,
    borderColor: colors.border,
    textAlign: 'center',
    fontSize: FONT_SIZE.xl,
    fontFamily: 'Inter_700Bold',
    color: colors.text,
  },
  otpInputFilled: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  otpInputError: {
    borderColor: colors.error,
  },
  errorText: {
    color: colors.error,
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_400Regular',
    textAlign: 'center',
    marginTop: -20,
    marginBottom: 20,
  },
  button: {
    backgroundColor: colors.primary,
    borderRadius: RADIUS.md,
    paddingVertical: SPACING.base,
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#fff',
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_600SemiBold',
  },
  resendButton: {
    marginTop: SPACING.xl,
    alignItems: 'center',
  },
  resendText: {
    fontSize: FONT_SIZE.sm,
    color: colors.primary,
    fontFamily: 'Inter_600SemiBold',
  },
  resendDisabled: {
    color: colors.textSecondary,
    fontFamily: 'Inter_400Regular',
  },
});
